'use client';

import { useState, useEffect, useMemo } from 'react';
import SearchBar from './SearchBar';
import MapFilters from './MapFilters';
import FacilityCard from './FacilityCard';
import Icon from '@/components/ui/AppIcon';

interface FilterState {
  facilityType: string[];
  services: string[];
  distance: number;
  accreditationOnly: boolean;
}

interface Facility {
  id: string;
  name: string;
  type: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
  website: string;
  distance: number;
  rating: number;
  reviewCount: number;
  services: string[];
  hours: string;
  isAccredited: boolean;
  accreditationDetails: string;
  description: string;
  image: string;
  alt: string;
  lat: number;
  lng: number;
  parkingInfo: string;
  accessibility: string[];
}

// Add haversine distance calculation function
const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 3959; // Radius of Earth in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
  Math.sin(dLat / 2) * Math.sin(dLat / 2) +
  Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
  Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const LocalResourcesInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({ lat: 38.9072, lng: -77.0369 });
  const [searchLocation, setSearchLocation] = useState('Washington, DC');
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    facilityType: [],
    services: [],
    distance: 25,
    accreditationOnly: true
  });
  const [sortBy, setSortBy] = useState<'distance' | 'rating'>('distance');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockFacilities: Facility[] = [
  {
    id: '1',
    name: 'Washington DC VA Medical Center',
    type: 'VA Medical Center',
    address: '50 Irving Street NW',
    city: 'Washington',
    state: 'DC',
    zip: '20422',
    phone: '(202) 745-8000',
    email: 'contact@dc.va.gov',
    website: 'https://www.va.gov/washington-dc-health-care',
    distance: 2.3,
    rating: 4.5,
    reviewCount: 1247,
    services: ['Primary Care', 'Mental Health', 'Emergency Services', 'Specialty Care', 'Pharmacy', 'Surgery'],
    hours: 'Mon-Fri: 7:00 AM - 7:00 PM, Sat-Sun: 8:00 AM - 4:00 PM',
    isAccredited: true,
    accreditationDetails: 'Fully accredited VA medical facility providing comprehensive healthcare services to veterans in the DC metro area.',
    description: 'The Washington DC VA Medical Center is a tertiary care facility providing comprehensive healthcare services to veterans. Our facility includes state-of-the-art medical equipment, experienced healthcare professionals, and a commitment to veteran-centered care. We offer a full range of services from primary care to specialized treatments.',
    image: "https://images.unsplash.com/photo-1485325267073-1b1bf0976638",
    alt: 'Modern multi-story medical center building with glass facade and American flag',
    lat: 38.9292,
    lng: -77.0119,
    parkingInfo: 'Free parking available in main lot. Valet parking for disabled veterans. Additional street parking nearby.',
    accessibility: ['Wheelchair Accessible', 'Elevator Access', 'Accessible Restrooms', 'Hearing Loop System', 'Sign Language Interpreters Available', 'Braille Signage']
  },
  {
    id: '2',
    name: 'American Legion Post 8',
    type: 'Veteran Service Organization',
    address: '3445 S Jefferson Street',
    city: 'Falls Church',
    state: 'VA',
    zip: '22041',
    phone: '(703) 845-1085',
    email: 'post8@legion.org',
    website: 'https://www.legion.org/post8',
    distance: 5.7,
    rating: 4.8,
    reviewCount: 342,
    services: ['Claims Assistance', 'Benefits Counseling', 'Community Support', 'Legal Referrals'],
    hours: 'Mon-Fri: 9:00 AM - 5:00 PM, Closed Weekends',
    isAccredited: true,
    accreditationDetails: 'VA-accredited veteran service organization with certified claims representatives on staff.',
    description: 'American Legion Post 8 has been serving veterans in the Falls Church area since 1919. Our accredited service officers provide free claims assistance and benefits counseling. We are committed to helping veterans navigate the VA system and receive the benefits they have earned through their service.',
    image: "https://images.unsplash.com/photo-1623730385193-af808a6419af",
    alt: 'Traditional brick building with American Legion emblem and American flag',
    lat: 38.8462,
    lng: -77.1872,
    parkingInfo: 'Free parking lot behind building. Street parking also available.',
    accessibility: ['Wheelchair Accessible', 'Accessible Entrance', 'Accessible Restrooms', 'Ground Floor Access']
  },
  {
    id: '3',
    name: 'Fort Belvoir Community Hospital',
    type: 'VA Outpatient Clinic',
    address: '9300 DeWitt Loop',
    city: 'Fort Belvoir',
    state: 'VA',
    zip: '22060',
    phone: '(571) 231-3224',
    email: 'info@belvoir.va.gov',
    website: 'https://www.va.gov/fort-belvoir',
    distance: 12.4,
    rating: 4.3,
    reviewCount: 856,
    services: ['Primary Care', 'Mental Health', 'Pharmacy', 'Lab Services', 'Radiology'],
    hours: 'Mon-Fri: 7:30 AM - 4:30 PM, Closed Weekends',
    isAccredited: true,
    accreditationDetails: 'VA-operated outpatient clinic providing primary care and specialty services.',
    description: 'Fort Belvoir Community Hospital VA Clinic provides comprehensive outpatient services to veterans in the Northern Virginia area. Our clinic offers primary care, mental health services, and various specialty care options in a modern, accessible facility.',
    image: "https://images.unsplash.com/photo-1628390707616-3940b65c686d",
    alt: 'Modern healthcare facility with large windows and landscaped entrance',
    lat: 38.7092,
    lng: -77.1447,
    parkingInfo: 'Ample free parking. Designated spaces for disabled veterans near entrance.',
    accessibility: ['Wheelchair Accessible', 'Automatic Doors', 'Accessible Restrooms', 'Elevator Access', 'Accessible Exam Rooms']
  },
  {
    id: '4',
    name: 'Veterans of Foreign Wars Post 1177',
    type: 'Veteran Service Organization',
    address: '8201 Arlington Boulevard',
    city: 'Fairfax',
    state: 'VA',
    zip: '22031',
    phone: '(703) 280-4444',
    email: 'post1177@vfw.org',
    website: 'https://www.vfw.org/post1177',
    distance: 8.9,
    rating: 4.7,
    reviewCount: 289,
    services: ['Claims Assistance', 'Benefits Counseling', 'Community Events', 'Veteran Advocacy'],
    hours: 'Mon-Thu: 10:00 AM - 6:00 PM, Fri: 10:00 AM - 8:00 PM, Sat: 12:00 PM - 6:00 PM',
    isAccredited: true,
    accreditationDetails: 'VA-accredited VFW post with experienced service officers trained in VA benefits and claims.',
    description: 'VFW Post 1177 serves veterans throughout Fairfax County with free claims assistance and benefits counseling. Our accredited service officers have decades of combined experience helping veterans secure their earned benefits. We also host community events and provide a supportive environment for veterans.',
    image: "https://images.unsplash.com/photo-1709940722745-7708b1504432",
    alt: 'Single-story building with VFW emblem and flags displayed prominently',
    lat: 38.8607,
    lng: -77.2219,
    parkingInfo: 'Free parking lot with ample spaces. Handicap parking near entrance.',
    accessibility: ['Wheelchair Accessible', 'Ramp Access', 'Accessible Restrooms', 'Wide Doorways']
  },
  {
    id: '5',
    name: 'Alexandria Vet Center',
    type: 'Vet Center',
    address: '6940 South Kings Highway, Suite 204',
    city: 'Alexandria',
    state: 'VA',
    zip: '22310',
    phone: '(703) 360-8633',
    email: 'alexandria.vetcenter@va.gov',
    website: 'https://www.va.gov/alexandria-vet-center',
    distance: 6.2,
    rating: 4.9,
    reviewCount: 178,
    services: ['Counseling', 'PTSD Treatment', 'Readjustment Counseling', 'Military Sexual Trauma Counseling', 'Bereavement Counseling'],
    hours: 'Mon-Fri: 8:00 AM - 6:00 PM, Sat: By Appointment',
    isAccredited: true,
    accreditationDetails: 'VA-operated Vet Center providing confidential readjustment counseling services.',
    description: 'Alexandria Vet Center provides free, confidential counseling services to veterans, service members, and their families. Our experienced counselors specialize in PTSD, military sexual trauma, bereavement, and readjustment issues. All services are provided in a non-medical, community-based setting.',
    image: "https://images.unsplash.com/photo-1563086037-b54ac7a4b325",
    alt: 'Professional office building with modern design and welcoming entrance',
    lat: 38.7689,
    lng: -77.1456,
    parkingInfo: 'Free parking in building lot. Additional street parking available.',
    accessibility: ['Wheelchair Accessible', 'Elevator Access', 'Accessible Restrooms', 'Quiet Counseling Rooms']
  },
  {
    id: '6',
    name: 'VA Regional Office - Washington DC',
    type: 'Regional Benefits Office',
    address: '1722 I Street NW',
    city: 'Washington',
    state: 'DC',
    zip: '20421',
    phone: '(800) 827-1000',
    email: 'dc.varo@va.gov',
    website: 'https://www.benefits.va.gov/washington',
    distance: 3.1,
    rating: 4.2,
    reviewCount: 634,
    services: ['Claims Processing', 'Benefits Counseling', 'Education Benefits', 'Home Loan Assistance', 'Vocational Rehabilitation'],
    hours: 'Mon-Fri: 8:00 AM - 4:30 PM, Closed Weekends',
    isAccredited: true,
    accreditationDetails: 'Official VA Regional Office processing claims and providing benefits services for DC, Maryland, and Virginia.',
    description: 'The Washington DC VA Regional Office processes disability compensation claims, pension claims, and provides comprehensive benefits counseling. Our staff can assist with education benefits, home loans, vocational rehabilitation, and other VA programs. Walk-in assistance available during business hours.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_128cff32b-1767542394310.png",
    alt: 'Government office building with classical architecture and VA seal',
    lat: 38.9022,
    lng: -77.0414,
    parkingInfo: 'Limited metered street parking. Public parking garage two blocks away.',
    accessibility: ['Wheelchair Accessible', 'Elevator Access', 'Accessible Restrooms', 'Security Screening', 'Sign Language Interpreters Available']
  },
  {
    id: '7',
    name: 'Bay Pines VA Healthcare System',
    type: 'VA Medical Center',
    address: '10000 Bay Pines Boulevard',
    city: 'Bay Pines',
    state: 'FL',
    zip: '33744',
    phone: '(727) 398-6661',
    email: 'contact@baypines.va.gov',
    website: 'https://www.va.gov/bay-pines-health-care',
    distance: 0,
    rating: 4.4,
    reviewCount: 2156,
    services: ['Primary Care', 'Mental Health', 'Emergency Services', 'Specialty Care', 'Pharmacy', 'Surgery', 'Rehabilitation'],
    hours: 'Mon-Fri: 7:00 AM - 7:00 PM, Sat-Sun: Emergency Only',
    isAccredited: true,
    accreditationDetails: 'Fully accredited VA medical facility serving veterans in the Tampa Bay and surrounding areas.',
    description: 'Bay Pines VA Healthcare System is one of the largest VA medical centers in Florida, providing comprehensive healthcare services to veterans. Our facility includes specialized care units, modern medical technology, and a dedicated team committed to veteran health and wellbeing.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14204d912-1765342957879.png",
    alt: 'Large modern medical center with palm trees and American flag',
    lat: 27.8139,
    lng: -82.7729,
    parkingInfo: 'Ample free parking available. Valet service for disabled veterans. Multiple parking lots throughout campus.',
    accessibility: ['Wheelchair Accessible', 'Elevator Access', 'Accessible Restrooms', 'Hearing Loop System', 'Sign Language Interpreters Available', 'Accessible Exam Rooms']
  },
  {
    id: '8',
    name: 'St. Petersburg VA Clinic',
    type: 'VA Outpatient Clinic',
    address: '9870 4th Street North',
    city: 'St. Petersburg',
    state: 'FL',
    zip: '33702',
    phone: '(727) 398-9443',
    email: 'info@stpete.va.gov',
    website: 'https://www.va.gov/st-petersburg-clinic',
    distance: 0,
    rating: 4.6,
    reviewCount: 487,
    services: ['Primary Care', 'Mental Health', 'Pharmacy', 'Lab Services', 'Telehealth'],
    hours: 'Mon-Fri: 8:00 AM - 4:30 PM, Closed Weekends',
    isAccredited: true,
    accreditationDetails: 'VA-operated outpatient clinic providing primary care and specialty services to St. Petersburg area veterans.',
    description: 'St. Petersburg VA Clinic offers convenient access to quality healthcare for veterans in the St. Petersburg area. Our clinic provides primary care, mental health services, and pharmacy services in a modern, patient-centered environment.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ab57ef35-1767440152590.png",
    alt: 'Single-story medical clinic building with modern architecture',
    lat: 27.8436,
    lng: -82.6537,
    parkingInfo: 'Free parking lot with designated spaces for disabled veterans.',
    accessibility: ['Wheelchair Accessible', 'Automatic Doors', 'Accessible Restrooms', 'Ground Floor Access', 'Accessible Exam Rooms']
  },
  {
    id: '9',
    name: 'American Legion Post 5',
    type: 'Veteran Service Organization',
    address: '1290 North Martin Luther King Jr Avenue',
    city: 'Clearwater',
    state: 'FL',
    zip: '33755',
    phone: '(727) 446-5607',
    email: 'post5@legion.org',
    website: 'https://www.legion.org/post5',
    distance: 0,
    rating: 4.7,
    reviewCount: 234,
    services: ['Claims Assistance', 'Benefits Counseling', 'Community Support', 'Legal Referrals', 'Job Assistance'],
    hours: 'Mon-Fri: 9:00 AM - 5:00 PM, Sat: 10:00 AM - 2:00 PM',
    isAccredited: true,
    accreditationDetails: 'VA-accredited veteran service organization with certified claims representatives serving Clearwater area veterans.',
    description: 'American Legion Post 5 has been proudly serving Clearwater area veterans for over 70 years. Our accredited service officers provide free claims assistance and benefits counseling. We host regular community events and maintain a strong support network for local veterans.',
    image: "https://images.unsplash.com/photo-1560536139-29a89e5dfbbe",
    alt: 'American Legion building with flags and veteran memorial',
    lat: 27.9778,
    lng: -82.7629,
    parkingInfo: 'Free parking lot available. Street parking also accessible.',
    accessibility: ['Wheelchair Accessible', 'Ramp Access', 'Accessible Restrooms', 'Ground Floor Meeting Rooms']
  },
  {
    id: '10',
    name: 'Clearwater Vet Center',
    type: 'Vet Center',
    address: '29259 US Highway 19 North, Suite 100',
    city: 'Clearwater',
    state: 'FL',
    zip: '33761',
    phone: '(727) 549-3633',
    email: 'clearwater.vetcenter@va.gov',
    website: 'https://www.va.gov/clearwater-vet-center',
    distance: 0,
    rating: 4.9,
    reviewCount: 156,
    services: ['Counseling', 'PTSD Treatment', 'Readjustment Counseling', 'Military Sexual Trauma Counseling', 'Bereavement Counseling', 'Family Counseling'],
    hours: 'Mon-Fri: 8:00 AM - 5:00 PM, Evening Hours by Appointment',
    isAccredited: true,
    accreditationDetails: 'VA-operated Vet Center providing confidential readjustment counseling services to combat veterans and their families.',
    description: 'Clearwater Vet Center provides free, confidential counseling services in a welcoming, non-medical environment. Our experienced counselors specialize in combat-related PTSD, readjustment issues, and family counseling. All services are free and available to eligible veterans and their families.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1020f041f-1767232351970.png",
    alt: 'Professional counseling office with comfortable waiting area',
    lat: 28.0398,
    lng: -82.7298,
    parkingInfo: 'Free parking in office complex lot.',
    accessibility: ['Wheelchair Accessible', 'Elevator Access', 'Accessible Restrooms', 'Private Counseling Rooms']
  },
  {
    id: '11',
    name: 'Veterans of Foreign Wars Post 4261',
    type: 'Veteran Service Organization',
    address: '4805 Park Boulevard',
    city: 'Pinellas Park',
    state: 'FL',
    zip: '33781',
    phone: '(727) 544-4281',
    email: 'post4261@vfw.org',
    website: 'https://www.vfw.org/post4261',
    distance: 0,
    rating: 4.8,
    reviewCount: 312,
    services: ['Claims Assistance', 'Benefits Counseling', 'Community Events', 'Veteran Advocacy', 'Social Activities'],
    hours: 'Mon-Sat: 10:00 AM - 10:00 PM, Sun: 12:00 PM - 8:00 PM',
    isAccredited: true,
    accreditationDetails: 'VA-accredited VFW post with trained service officers assisting veterans with benefits and claims.',
    description: 'VFW Post 4261 serves the Pinellas Park and surrounding communities with comprehensive veteran services. Our accredited service officers provide free assistance with VA claims and benefits. We maintain an active social calendar and support network for veterans and their families.',
    image: "https://images.unsplash.com/photo-1709940722745-7708b1504432",
    alt: 'VFW post building with American and VFW flags',
    lat: 27.8528,
    lng: -82.7037,
    parkingInfo: 'Large parking lot with ample spaces. Handicap parking near entrance.',
    accessibility: ['Wheelchair Accessible', 'Ramp Access', 'Accessible Restrooms', 'Wide Doorways', 'Accessible Meeting Rooms']
  },
  {
    id: '12',
    name: 'Tampa VA Clinic',
    type: 'VA Outpatient Clinic',
    address: '3637 West Waters Avenue',
    city: 'Tampa',
    state: 'FL',
    zip: '33614',
    phone: '(813) 558-3939',
    email: 'info@tampa.va.gov',
    website: 'https://www.va.gov/tampa-clinic',
    distance: 0,
    rating: 4.5,
    reviewCount: 678,
    services: ['Primary Care', 'Mental Health', 'Pharmacy', 'Lab Services', 'Radiology', 'Physical Therapy'],
    hours: 'Mon-Fri: 7:30 AM - 4:30 PM, Closed Weekends',
    isAccredited: true,
    accreditationDetails: 'VA-operated outpatient clinic providing comprehensive primary care and specialty services.',
    description: 'Tampa VA Clinic provides accessible healthcare services to veterans in the greater Tampa area. Our clinic offers a full range of outpatient services including primary care, mental health, and diagnostic services in a modern facility with experienced staff.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a6dfc495-1767057136376.png",
    alt: 'Modern medical clinic building with landscaping',
    lat: 28.0153,
    lng: -82.5112,
    parkingInfo: 'Free parking available. Designated spaces for disabled veterans near entrance.',
    accessibility: ['Wheelchair Accessible', 'Automatic Doors', 'Accessible Restrooms', 'Elevator Access', 'Accessible Exam Rooms']
  }];

  // Calculate distances dynamically based on current location
  const facilitiesWithDistances = useMemo(() => {
    return mockFacilities.map((facility) => ({
      ...facility,
      distance: calculateDistance(
        currentLocation.lat,
        currentLocation.lng,
        facility.lat,
        facility.lng
      )
    }));
  }, [currentLocation]);

  const filteredFacilities = facilitiesWithDistances.
  filter((facility) => {
    if (filters.facilityType.length > 0) {
      const typeMap: Record<string, string> = {
        'va-medical': 'VA Medical Center',
        'va-clinic': 'VA Outpatient Clinic',
        'vso': 'Veteran Service Organization',
        'vet-center': 'Vet Center',
        'benefits-office': 'Regional Benefits Office'
      };
      const matchesType = filters.facilityType.some((type) => typeMap[type] === facility.type);
      if (!matchesType) return false;
    }

    if (filters.services.length > 0) {
      const matchesService = filters.services.some((service) =>
      facility.services.includes(service)
      );
      if (!matchesService) return false;
    }

    if (facility.distance > filters.distance) return false;

    if (filters.accreditationOnly && !facility.isAccredited) return false;

    return true;
  }).
  sort((a, b) => {
    if (sortBy === 'distance') return a.distance - b.distance;
    return b.rating - a.rating;
  });

  const handleSearch = async (location: string) => {
    if (!location.trim()) return;

    setIsSearching(true);
    setLocationError(null);

    try {
      // Use Google Geocoding API or a public geocoding service
      const geocodeUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(location)}&limit=1`;

      const response = await fetch(geocodeUrl, {
        headers: {
          'User-Agent': 'VeteranResourcesApp/1.0'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to geocode location');
      }

      const data = await response.json();

      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        setCurrentLocation({
          lat: parseFloat(lat),
          lng: parseFloat(lon)
        });
        setSearchLocation(display_name || location);
        setLocationError(null);
      } else {
        setLocationError(`Could not find location: "${location}". Please try a different search term (e.g., city and state, or ZIP code).`);
      }
    } catch (error) {
      setLocationError('Unable to search for this location. Please check your internet connection and try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleUseCurrentLocation = () => {
    setLocationError(null);
    setIsLoadingLocation(true);

    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      setIsLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setCurrentLocation({ lat: latitude, lng: longitude });
        setSearchLocation('Current Location');
        setIsLoadingLocation(false);
      },
      (error) => {
        let errorMessage = 'Unable to retrieve your location';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.';
            break;
        }
        setLocationError(errorMessage);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleGetDirections = (lat: number, lng: number) => {
    if (isHydrated && typeof window !== 'undefined') {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
    }
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:527:6" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="527" data-component-end-line="534" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-background%20pb-12%22%7D" className="bg-background pb-12">
        <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:528:8" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="528" data-component-end-line="533" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:529:10" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="529" data-component-end-line="532" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20py-12%22%7D" className="text-center py-12">
            <Icon data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:530:12" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="530" data-component-end-line="530" data-component-file="LocalResourcesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MapIcon%22%2C%22className%22%3A%22text-text-secondary%20mx-auto%20mb-4%22%7D" name="MapIcon" size={48} className="text-text-secondary mx-auto mb-4" />
            <p data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:531:12" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="531" data-component-end-line="531" data-component-file="LocalResourcesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%22%2C%22textContent%22%3A%22Loading%20resources...%22%7D" className="text-text-secondary">Loading resources...</p>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:539:4" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="539" data-component-end-line="635" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-background%20pb-12%22%7D" className="bg-background pb-12">
      <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:540:6" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="540" data-component-end-line="634" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Search */}
        <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:543:8" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="543" data-component-end-line="561" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%20pt-8%22%7D" className="mb-6 pt-8">
          <SearchBar data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:544:10" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="544" data-component-end-line="547" data-component-file="LocalResourcesInteractive.tsx" data-component-name="SearchBar" data-component-content="%7B%22elementName%22%3A%22SearchBar%22%7D"
            onSearch={handleSearch}
            onUseCurrentLocation={handleUseCurrentLocation}
            isLoadingLocation={isLoadingLocation || isSearching} />

          {locationError &&
          <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:550:10" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="550" data-component-end-line="553" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-3%20p-3%20bg-destructive%20bg-opacity-10%20border%20border-destructive%20rounded%20flex%20items-start%20space-x-2%22%7D" className="mt-3 p-3 bg-destructive bg-opacity-10 border border-destructive rounded flex items-start space-x-2">
              <Icon data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:551:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="551" data-component-end-line="551" data-component-file="LocalResourcesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ExclamationTriangleIcon%22%2C%22className%22%3A%22text-destructive%20flex-shrink-0%20mt-0.5%22%7D" name="ExclamationTriangleIcon" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
              <p data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:552:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="552" data-component-end-line="552" data-component-file="LocalResourcesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-destructive%22%7D" className="text-sm text-destructive">{locationError}</p>
            </div>
          }
          {isSearching &&
          <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:556:10" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="556" data-component-end-line="559" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-3%20p-3%20bg-primary%20bg-opacity-10%20border%20border-primary%20rounded%20flex%20items-center%20space-x-2%22%7D" className="mt-3 p-3 bg-primary bg-opacity-10 border border-primary rounded flex items-center space-x-2">
              <Icon data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:557:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="557" data-component-end-line="557" data-component-file="LocalResourcesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowPathIcon%22%2C%22className%22%3A%22text-primary%20animate-spin%22%7D" name="ArrowPathIcon" size={20} className="text-primary animate-spin" />
              <p data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:558:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="558" data-component-end-line="558" data-component-file="LocalResourcesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-primary%22%2C%22textContent%22%3A%22Searching%20for%20location...%22%7D" className="text-sm text-primary">Searching for location...</p>
            </div>
          }
        </div>

        {/* Filters */}
        <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:564:8" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="564" data-component-end-line="566" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%22%7D" className="mb-6">
          <MapFilters data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:565:10" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="565" data-component-end-line="565" data-component-file="LocalResourcesInteractive.tsx" data-component-name="MapFilters" data-component-content="%7B%22elementName%22%3A%22MapFilters%22%7D" onFilterChange={handleFilterChange} />
        </div>

        {/* Results Header */}
        <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:569:8" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="569" data-component-end-line="590" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-6%20flex%20flex-col%20sm%3Aflex-row%20sm%3Aitems-center%20sm%3Ajustify-between%20gap-4%22%7D" className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:570:10" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="570" data-component-end-line="577" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <h2 data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:571:12" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="571" data-component-end-line="573" data-component-file="LocalResourcesInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-cta%20font-semibold%20text-xl%20text-text-primary%22%2C%22textContent%22%3A%22Facilities%20Found%22%7D" className="font-cta font-semibold text-xl text-text-primary">
              {filteredFacilities.length} Facilities Found
            </h2>
            <p data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:574:12" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="574" data-component-end-line="576" data-component-file="LocalResourcesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-text-secondary%20mt-1%22%2C%22textContent%22%3A%22Showing%20results%20near%22%7D" className="text-sm text-text-secondary mt-1">
              Showing results near {searchLocation}
            </p>
          </div>

          <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:579:10" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="579" data-component-end-line="589" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%22%7D" className="flex items-center space-x-2">
            <span data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:580:12" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="580" data-component-end-line="580" data-component-file="LocalResourcesInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-text-secondary%22%2C%22textContent%22%3A%22Sort%20by%3A%22%7D" className="text-sm text-text-secondary">Sort by:</span>
            <select data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:581:12" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="581" data-component-end-line="588" data-component-file="LocalResourcesInteractive.tsx" data-component-name="select" data-component-content="%7B%22elementName%22%3A%22select%22%2C%22value%22%3A%22%5Bvar%3AsortBy%5D%22%2C%22className%22%3A%22px-3%20py-2%20border%20border-border%20rounded%20text-sm%20text-text-primary%20focus%3Aoutline-none%20focus%3Aring-2%20focus%3Aring-primary%22%7D"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'distance' | 'rating')}
              className="px-3 py-2 border border-border rounded text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary">

              <option data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:586:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="586" data-component-end-line="586" data-component-file="LocalResourcesInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22distance%22%2C%22textContent%22%3A%22Distance%22%7D" value="distance">Distance</option>
              <option data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:587:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="587" data-component-end-line="587" data-component-file="LocalResourcesInteractive.tsx" data-component-name="option" data-component-content="%7B%22elementName%22%3A%22option%22%2C%22value%22%3A%22rating%22%2C%22textContent%22%3A%22Rating%22%7D" value="rating">Rating</option>
            </select>
          </div>
        </div>

        {/* Facility List - Full Width */}
        <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:593:8" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="593" data-component-end-line="613" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-4%22%7D" className="space-y-4">
          {filteredFacilities.length === 0 ?
          <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:595:10" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="595" data-component-end-line="603" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded%20shadow-subtle%20border%20border-border%20p-8%20text-center%22%7D" className="bg-card rounded shadow-subtle border border-border p-8 text-center">
              <Icon data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:596:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="596" data-component-end-line="596" data-component-file="LocalResourcesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MapPinIcon%22%2C%22className%22%3A%22text-text-secondary%20mx-auto%20mb-4%22%7D" name="MapPinIcon" size={48} className="text-text-secondary mx-auto mb-4" />
              <h3 data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:597:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="597" data-component-end-line="599" data-component-file="LocalResourcesInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-cta%20font-semibold%20text-lg%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22No%20Facilities%20Found%22%7D" className="font-cta font-semibold text-lg text-text-primary mb-2">
                No Facilities Found
              </h3>
              <p data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:600:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="600" data-component-end-line="602" data-component-file="LocalResourcesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-text-secondary%20mb-4%22%2C%22textContent%22%3A%22Try%20adjusting%20your%20filters%20or%20search%20location%20to%20find%20more%20results.%22%7D" className="text-sm text-text-secondary mb-4">
                Try adjusting your filters or search location to find more results.
              </p>
            </div> :

          filteredFacilities.map((facility) =>
          <FacilityCard data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:606:10" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="606" data-component-end-line="609" data-component-file="LocalResourcesInteractive.tsx" data-component-name="FacilityCard" data-component-content="%7B%22elementName%22%3A%22FacilityCard%22%7D"
            key={facility.id}
            facility={facility}
            onGetDirections={handleGetDirections} />

          )
          }
        </div>

        {/* Trust Section */}
        <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:616:8" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="616" data-component-end-line="633" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-12%20p-6%20bg-green-50%20border%20border-green-300%20rounded%22%7D" className="mt-12 p-6 bg-green-50 border border-green-300 rounded">
          <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:617:10" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="617" data-component-end-line="632" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20space-x-4%22%7D" className="flex items-start space-x-4">
            <Icon data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:618:12" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="618" data-component-end-line="618" data-component-file="LocalResourcesInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ShieldCheckIcon%22%2C%22className%22%3A%22text-green-700%20flex-shrink-0%22%7D" name="ShieldCheckIcon" variant="solid" size={32} className="text-green-700 flex-shrink-0" />
            <div data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:619:12" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="619" data-component-end-line="631" data-component-file="LocalResourcesInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h3 data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:620:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="620" data-component-end-line="622" data-component-file="LocalResourcesInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-cta%20font-semibold%20text-lg%20text-green-900%20mb-2%22%2C%22textContent%22%3A%22All%20Organizations%20Are%20Verified%22%7D" className="font-cta font-semibold text-lg text-green-900 mb-2">
                All Organizations Are Verified
              </h3>
              <p data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:623:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="623" data-component-end-line="626" data-component-file="LocalResourcesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-green-800%20mb-3%22%2C%22textContent%22%3A%22Every%20veteran%20service%20organization%20listed%20here%20is%20VA-accredited%20and%20verified.%20We%20only%20show%20legitimate%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20organizations%20that%20meet%20VA%20standards%20for%20claims%20representation%20and%20benefits%20counseling.%22%7D" className="text-sm text-green-800 mb-3">
                Every veteran service organization listed here is VA-accredited and verified. We only show legitimate 
                organizations that meet VA standards for claims representation and benefits counseling.
              </p>
              <p data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:627:14" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="627" data-component-end-line="630" data-component-file="LocalResourcesInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-green-800%22%2C%22textContent%22%3A%22Be%20cautious%20of%20organizations%20not%20listed%20here%20that%20charge%20upfront%20fees%20or%20%5Cn%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20make%20guaranteed%20outcome%20promises.%20Legitimate%20VSOs%20provide%20free%20assistance%20to%20veterans.%22%7D" className="text-sm text-green-800">
                <strong data-component-id="src/app/local-resources-map/components/LocalResourcesInteractive.tsx:628:16" data-component-path="src/app/local-resources-map/components/LocalResourcesInteractive.tsx" data-component-line="628" data-component-end-line="628" data-component-file="LocalResourcesInteractive.tsx" data-component-name="strong" data-component-content="%7B%22elementName%22%3A%22strong%22%2C%22textContent%22%3A%22Warning%3A%22%7D">Warning:</strong> Be cautious of organizations not listed here that charge upfront fees or 
                make guaranteed outcome promises. Legitimate VSOs provide free assistance to veterans.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default LocalResourcesInteractive;